import { Link } from 'react-router-dom';
import { useT } from '../../i18n';
import { L } from '../tokens';
import { Card } from './ReportParts';
import MoneyCard from './MoneyCard';
import { countText, moneyText } from './reportFormat';

// The pieces the four "who" pages share (Suppliers, Supplier page, Customers, Customer page), as Leo's updated boards draw them:
// two money cards (Orders in gold, Invoiced in blue), Documents per type, Discrepancies identified with Documents late and Price increases apart ("not in total"), and a ranking by order value.
export const ORDERS_COLOR = '#C9A23A';
export const INVOICED_COLOR = '#4A6A8A';
const DOC_ROWS = ['order', 'confirmation', 'packing_list', 'invoice', 'delivery_note'];

export function MoneyPair({ data, prefix, phone }) {
  const t = useT();
  return (
    <div style={{ display: 'grid', gridTemplateColumns: phone ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: 18 }}>
      <MoneyCard testid={`${prefix}-orders`} title={t('reports.who.orders')} total={data.orders.total} currency={data.currency} series={data.orders.series} grain={data.period.grain} color={ORDERS_COLOR}
        sub={data.orders.notes && data.orders.notes.without_value > 0 ? t('reports.who.ordersNoValue', { n: countText(data.orders.notes.without_value) }) : null} />
      <MoneyCard testid={`${prefix}-invoiced`} title={t('reports.invoiced')} total={data.invoiced.total} currency={data.currency} series={data.invoiced.series} grain={data.period.grain} color={INVOICED_COLOR} />
    </div>
  );
}

// Documents: the count per type. Confirmations are stored as purchase orders, so they cannot be counted apart: a dash.
export function DocumentsCard({ documents, prefix }) {
  const t = useT();
  return (
    <Card title={t('reports.who.documents')} testid={`${prefix}-documents`}>
      {DOC_ROWS.map((k) => (
        <div key={k} data-testid={`${prefix}-docs-${k}`} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderTop: `1px solid ${L.rowLine}`, fontSize: 15 }}>
          <span>{t(`reports.who.doc.${k}`)}</span><strong>{documents[k] == null ? t('reports.none') : countText(documents[k])}</strong>
        </div>
      ))}
    </Card>
  );
}

// Discrepancies identified: on a list page the suppliers or customers with their count and amount; on a company page the types found. Below, apart and "not in total":
// Documents late (POs without an invoice) and Price increases.
export function ProblemsCard({ problems, currency, prefix, who = null }) {
  const t = useT();
  const d = problems.discrepancies;
  const line = (key, label, value) => (
    <div key={key} data-testid={`${prefix}-${key}`} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: 15, color: L.muted }}><span>{label}</span><span style={{ color: L.text, fontWeight: 600 }}>{value}</span></div>
  );
  return (
    <Card title={t('reports.who.discrepancies')} testid={`${prefix}-discrepancies`}>
      {who ? d.who.map((x, i) => {
        const e = who === 'supplier' ? x.supplier : x.customer;
        return (
          <div key={e ? e.id : i} data-testid={`${prefix}-disc-who`} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderTop: `1px solid ${L.rowLine}`, fontSize: 15 }}>
            <span>{e && e.name ? e.name : t('reports.none')}</span><strong>{countText(x.cases)} · {moneyText(x.amount, currency)}</strong>
          </div>
        );
      }) : d.rows.map((r) => (
        <div key={r.row} data-testid={`${prefix}-disc-${r.row}`} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderTop: `1px solid ${L.rowLine}`, fontSize: 15 }}>
          <span>{t(`reports.disc.row.${r.row}`)}</span><strong>{countText(r.cases)}{r.cases_with_money > 0 ? ` · ${moneyText(r.amount, currency)}` : ''}</strong>
        </div>
      ))}
      <div style={{ borderTop: `1px solid ${L.border}`, marginTop: 8, paddingTop: 6 }}>
        {line('late', t('reports.who.late'), t('reports.who.posWithoutInvoice', { n: countText(problems.documents_late.pos_without_invoice) }))}
        {line('prices', t('reports.who.prices'), `${countText(problems.price_increases.items)} · +${moneyText(problems.price_increases.added_cost, currency)}`)}
      </div>
    </Card>
  );
}

// A ranking table by order value. columns: [{ key, label, money? }]; rows carry `name`, `to` (a link or null), `bar` and the column values.
export function RankingTable({ title, columns, rows, currency, testid, noteRow = null }) {
  const t = useT();
  const th = { textAlign: 'right', fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', color: L.muted, fontWeight: 600, padding: '8px 10px', borderBottom: `1px solid ${L.border}`, whiteSpace: 'nowrap' };
  const td = { padding: '10px 10px', borderBottom: `1px solid ${L.rowLine}`, textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };
  return (
    <Card title={title} testid={testid}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 15 }}>
          <thead><tr><th style={{ ...th, textAlign: 'left' }} /><th style={{ ...th, width: '30%' }} />{columns.map((c) => <th key={c.key} style={th}>{c.label}</th>)}</tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-testid={r.testid}>
                <td style={{ ...td, textAlign: 'left', whiteSpace: 'normal' }}>{r.to ? <Link to={r.to} style={{ color: L.text, textDecoration: 'none' }}>{r.name}</Link> : r.name}</td>
                <td style={td}><span aria-hidden="true" style={{ display: 'block', height: 8, borderRadius: 4, background: L.rowLine, position: 'relative' }}><span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${r.bar || 0}%`, borderRadius: 4, background: '#4F6B30' }} /></span></td>
                {columns.map((c) => <td key={c.key} style={{ ...td, ...(c.strong ? { fontWeight: 600 } : null), ...(c.green ? { color: L.green } : null) }}>{r[c.key] == null ? t('reports.none') : c.money ? moneyText(r[c.key], currency) : countText(r[c.key])}</td>)}
              </tr>
            ))}
            {noteRow}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
